import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Stack,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  LinearProgress,
  Grid,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import {
  Save as SaveIcon,
  PhotoCamera as PhotoIcon,
  Edit as EditIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Web as WebIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { shelterService } from '@services/sheltersService';

interface ShelterSettings {
  name: string;
  description?: string;
  address: {
    street?: string;
    city?: string;
    county?: string;
    zipCode?: string;
    country?: string;
  };
  phone?: string;
  email?: string;
  website?: string;
  isActive: boolean;
  acceptsNewAnimals: boolean;
  publicProfile: boolean;
  adoptionProcess: string;
  requirements: string;
  photos: string[];
}

const ShelterSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [settings, setSettings] = useState<ShelterSettings>({
    name: '',
    description: '',
    address: {
      street: '',
      city: '',
      county: '',
      zipCode: '',
      country: 'România'
    },
    phone: '',
    email: '',
    website: '',
    isActive: true,
    acceptsNewAnimals: true,
    publicProfile: true,
    adoptionProcess: '',
    requirements: '',
    photos: []
  });
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      
      if (!user?.shelterId) {
        throw new Error('Shelter ID not found');
      }

      const response = await shelterService.getShelterProfile(user.shelterId);
      
      // Transformă răspunsul backend-ului în structura așteptată de frontend
      setSettings({
        name: response.name || '',
        description: response.description || '',
        address: {
          street: response.address || '',
          city: response.city || '',
          county: response.county || '',
          zipCode: response.zip_code || '',
          country: response.country || 'România'
        },
        phone: response.phone || '',
        email: response.email || '',
        website: response.website || '',
        isActive: response.status === 'active',
        acceptsNewAnimals: true, // Placeholder
        publicProfile: true, // Placeholder
        adoptionProcess: '', // Placeholder
        requirements: '', // Placeholder
        photos: response.photos?.map((photo: any) => photo.url) || []
      });
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (!user?.shelterId) {
        throw new Error('Shelter ID not found');
      }

      // Transformă datele frontend în formatul așteptat de backend
      const backendData = {
        name: settings.name,
        description: settings.description,
        address: settings.address.street,
        city: settings.address.city,
        county: settings.address.county,
        zip_code: settings.address.zipCode,
        country: settings.address.country,
        phone: settings.phone,
        email: settings.email,
        website: settings.website,
        status: settings.isActive ? 'active' : 'inactive'
      } as any; // Cast pentru a evita erorile de tip

      await shelterService.updateShelterProfile(user.shelterId, backendData);
      setSnackbar({ open: true, message: 'Setările au fost salvate cu succes!', severity: 'success' });
    } catch (error) {
      console.error('Error saving settings:', error);
      setSnackbar({ open: true, message: 'Eroare la salvarea setărilor', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof ShelterSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        setSaving(true);
        
        const formData = new FormData();
        formData.append('image', file);
        formData.append('type', 'gallery');
        formData.append('caption', '');
        
        const response = await fetch('/api/v1/shelter/photos/upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        });
        
        if (response.ok) {
          const result = await response.json();
          setSettings(prev => ({
            ...prev,
            photos: [...(prev.photos || []), result.data.url]
          }));
          setSnackbar({ open: true, message: 'Poză încărcată cu succes!', severity: 'success' });
        } else {
          throw new Error('Eroare la upload');
        }
      } catch (error) {
        console.error('Error uploading photo:', error);
        setSnackbar({ open: true, message: 'Eroare la încărcarea pozei', severity: 'error' });
      } finally {
        setSaving(false);
      }
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Setări Adapost
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gestionează informațiile și configurarea adapostului
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Basic Information */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={3}>
                Informații de Bază
              </Typography>
              
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Nume Adapost"
                  value={settings.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                />
                
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Descriere"
                  value={settings.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Descrie adapostul, misiunea și activitatea..."
                />

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Oraș"
                      value={settings.address.city || ''}
                      onChange={(e) => handleInputChange('address', { ...settings.address, city: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Județ"
                      value={settings.address.county || ''}
                      onChange={(e) => handleInputChange('address', { ...settings.address, county: e.target.value })}
                      required
                    />
                  </Grid>
                </Grid>

                <TextField
                  fullWidth
                  label="Adresă"
                  value={settings.address.street || ''}
                  onChange={(e) => handleInputChange('address', { ...settings.address, street: e.target.value })}
                  required
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={3}>
                Informații de Contact
              </Typography>
              
              <Stack spacing={3}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Telefon"
                      value={settings.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      InputProps={{
                        startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={settings.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      InputProps={{
                        startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      }}
                    />
                  </Grid>
                </Grid>

                <TextField
                  fullWidth
                  label="Website"
                  value={settings.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  InputProps={{
                    startAdornment: <WebIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Adoption Information */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={3}>
                Informații Adopție
              </Typography>
              
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Proces de Adopție"
                  value={settings.adoptionProcess}
                  onChange={(e) => handleInputChange('adoptionProcess', e.target.value)}
                  placeholder="Descrie procesul de adopție, pașii necesari..."
                />

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Cerințe pentru Adopție"
                  value={settings.requirements}
                  onChange={(e) => handleInputChange('requirements', e.target.value)}
                  placeholder="Listează cerințele pentru adoptatori..."
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Settings & Photos */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Settings */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={3}>
                Configurări
              </Typography>
              
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.isActive}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    />
                  }
                  label="Adapost Activ"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.acceptsNewAnimals}
                      onChange={(e) => handleInputChange('acceptsNewAnimals', e.target.checked)}
                    />
                  }
                  label="Acceptă Animale Noi"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.publicProfile}
                      onChange={(e) => handleInputChange('publicProfile', e.target.checked)}
                    />
                  }
                  label="Profil Public"
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Photos */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={3}>
                Fotografii
              </Typography>
              
              <Stack spacing={2}>
                {settings.photos.length > 0 ? (
                  <Box>
                    {settings.photos.map((photo, index) => (
                      <Box key={index} display="flex" alignItems="center" gap={1} mb={1}>
                        <Avatar src={photo} variant="rounded" />
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                          Foto {index + 1}
                        </Typography>
                        <IconButton size="small">
                          <EditIcon />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Alert severity="info">
                    Nu ai fotografii adăugate
                  </Alert>
                )}
                
                <Button
                  variant="outlined"
                  startIcon={<PhotoIcon />}
                  onClick={() => setPhotoDialogOpen(true)}
                  fullWidth
                >
                  Adaugă Fotografii
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Save Button */}
      <Box mt={4} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          size="large"
        >
          {saving ? 'Salvez...' : 'Salvează Setările'}
        </Button>
      </Box>

      {/* Photo Upload Dialog */}
      <Dialog open={photoDialogOpen} onClose={() => setPhotoDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adaugă Fotografii</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
              style={{ display: 'none' }}
              id="photo-upload"
            />
            <label htmlFor="photo-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<PhotoIcon />}
                fullWidth
              >
                Alege Fotografii
              </Button>
            </label>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhotoDialogOpen(false)}>Anulează</Button>
          <Button variant="contained">Adaugă</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pentru notificări */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ShelterSettingsPage;
